'use client';

import VerticalNavItem from 'components/admin/navigation/verticalNavItem';
import { Drawer, Sidebar, SidebarItemGroup, SidebarItems } from 'flowbite-react';
import { find, isString } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiChevronDoubleLeft, HiChevronDoubleRight } from 'react-icons/hi';
import { useNavigate } from 'react-router';
import { getVisibleNavItems } from 'src/components/admin/navigation/vertical.config';
import { useAbility } from 'src/acl/useAbility';
import { useAuth } from 'src/contexts/AuthContext';
import { useGetFraudOpenCount } from 'src/hooks/useFraudWatch';
import { routePaths } from 'src/utils/constants';
import { themeConfigs } from 'src/utils/theme';

const drawerTheme = {
   root: {
      base: 'relative p-0 !h-full transition-transform bg-tertiary z-99 hidden md:block',
      position: {
         left: {
            off: 'left-0 top-0 w-[66px] -translate-x-1',
            on: 'w-60',
         },
      },
   },
};

const sidebarTheme = {
   root: {
      base: 'relative z-0 justify-between top-0 left-0 h-screen bg-tertiary sm:translate-x-0 !min-h-full !w-full !h-full',
      inner: 'rounded-none px-0 pb-0 bg-tertiary !min-h-full !h-full !pt-2.5',
   },
};

const VerticalNav = () => {
   const [t] = useTranslation();
   const navigate = useNavigate();
   const [isCollapsed, setIsCollapsed] = useState(false);

   const { isAdmin } = useAuth();
   const { data: openFraudReviews = 0 } = useGetFraudOpenCount(isAdmin);
   // NPT-2144: platform admins get the platform list, org admins only their org area.
   const ability = useAbility();
   const navItems = useMemo(() => getVisibleNavItems(ability), [ability]);

   useEffect(() => {
      function handleResize() {
         const isTablet = window.matchMedia(`(min-width: ${themeConfigs.screens.md}) and (max-width: 1023.98px)`).matches;
         if (isTablet) {
            setIsCollapsed(true);
         }
      }

      window.addEventListener('resize', handleResize);
      handleResize();

      return () => window.removeEventListener('resize', handleResize);
   }, []);

   const isPageActive = (page: string | string[]) => {
      const url = location.pathname;

      if (page === '/') {
         return url === '/';
      } else if (isString(page)) {
         return url.indexOf(page) === 0;
      } else {
         return !!find(page, (route: string) => url.startsWith(route));
      }
   };

   return (
      <Drawer id="vertical-sidebar" backdrop={false} onClose={() => setIsCollapsed(true)} open={!isCollapsed} position="left" theme={drawerTheme}>
         <Sidebar className='' aria-label={t('a11y.adminNavigation')} theme={sidebarTheme}>
            <SidebarItems className="flex flex-col justify-between bg-tertiary h-full">
               <SidebarItemGroup className={`bg-tertiary px-2.5 *:!mt-0.5 ${isCollapsed === true ? '!pr-1.5' : ''}`}>
                  {navItems.map((navItem, index) => {
                     const { icon: Icon, iconProps, activeIcon: ActiveIcon, activeIconProps, label } = navItem;

                     const isFraudWatch = navItem.route === routePaths.ADMIN.FRAUD_WATCH.INDEX;

                     return (
                        <VerticalNavItem
                           key={index}
                           active={navItem.exact ? location.pathname === navItem.route : isPageActive(navItem.route)}
                           activeIcon={ActiveIcon && <ActiveIcon color="white" size={20} {...activeIconProps} />}
                           badgeCount={isFraudWatch ? openFraudReviews : undefined}
                           badgeLabel={isFraudWatch ? t('admin.fraudWatch.openReviews', { count: openFraudReviews }) : undefined}
                           collapsed={isCollapsed}
                           icon={Icon && <Icon color="white" size={20} {...iconProps} />}
                           label={t(label)}
                           onClick={() => navigate(navItem.route)}
                        />
                     );
                  })}
               </SidebarItemGroup>
               <SidebarItemGroup
                  theme={{
                     base: 'hidden lg:flex relative justify-end items-center border-none m-0 px-4 py-0 h-14 hover:bg-activeSidebar cursor-pointer',
                  }}
               >
                  <button className="flex items-center justify-end w-full h-full px-2" onClick={() => setIsCollapsed(!isCollapsed)}>
                     {isCollapsed === true ? <HiChevronDoubleRight color="white" size={16} /> : <HiChevronDoubleLeft color="white" size={16} />}
                  </button>
               </SidebarItemGroup>
            </SidebarItems>
         </Sidebar>
      </Drawer>
   );
};

export default VerticalNav;
